import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Plus, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { invoiceService } from '../../services/invoice.service';
import { Invoice, InvoiceStatus } from '../../types';
import { GenerateInvoiceModal } from './GenerateInvoiceModal';

export const InvoicesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [useDateRange, setUseDateRange] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: invoicesAll, isLoading: loadingAll, error: errorAll } = useQuery({
    queryKey: ['invoices'],
    queryFn: invoiceService.getAll,
    enabled: !useDateRange,
  });

  const {
    data: invoicesRange,
    isLoading: loadingRange,
    error: errorRange,
  } = useQuery({
    queryKey: ['invoices', 'date-range', startDate, endDate],
    queryFn: () => invoiceService.getByDateRange(startDate, endDate),
    enabled: useDateRange && Boolean(startDate) && Boolean(endDate),
  });

  const invoices = useMemo<Invoice[]>(() => {
    return (useDateRange ? invoicesRange : invoicesAll) || [];
  }, [invoicesAll, invoicesRange, useDateRange]);

  const isLoading = useDateRange ? loadingRange : loadingAll;
  const error = useDateRange ? errorRange : errorAll;

  const issueMutation = useMutation({
    mutationFn: (id: number) => invoiceService.issue(id),
    onSuccess: async () => {
      toast.success('Invoice issued');
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      await queryClient.invalidateQueries({ queryKey: ['invoices', 'date-range'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to issue invoice');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
  };

  const statusBadgeVariant: Record<InvoiceStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    [InvoiceStatus.DRAFT]: 'warning',
    [InvoiceStatus.ISSUED]: 'info',
    [InvoiceStatus.PAID]: 'success',
    [InvoiceStatus.CANCELLED]: 'error',
  };

  return (
    <div className="space-y-6">
      <Card
        title="Invoices"
        action={
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={useDateRange ? 'secondary' : 'ghost'}
                onClick={() => setUseDateRange(false)}
              >
                All
              </Button>
              <Button
                size="sm"
                variant={useDateRange ? 'ghost' : 'secondary'}
                onClick={() => setUseDateRange(true)}
              >
                Date Range
              </Button>
            </div>

            {useDateRange && (
              <div className="flex items-center gap-2">
                <div className="w-40">
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div className="w-40">
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </div>
            )}

            <Button size="sm" onClick={() => setIsGenerateOpen(true)}>
              <Plus size={16} className="mr-2" />
              Generate
            </Button>
          </div>
        }
      >
        {isLoading && <div className="text-sm text-gray-600">Loading...</div>}
        {!isLoading && error && <div className="text-sm text-error">Failed to load invoices.</div>}

        {!isLoading && !error && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Trip</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={7}>
                    No invoices found.
                  </td>
                </TableRow>
              ) : (
                invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.invoiceNumber}</TableCell>
                    <TableCell>{inv.tripNumber}</TableCell>
                    <TableCell>
                      {inv.invoiceDate ? format(parseISO(inv.invoiceDate), 'MMM dd, yyyy') : '-'}
                    </TableCell>
                    <TableCell>{inv.clientName}</TableCell>
                    <TableCell>{formatCurrency(inv.totalAmount)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant[inv.status]}>{inv.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {inv.status === InvoiceStatus.DRAFT ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={issueMutation.isPending}
                          onClick={() => issueMutation.mutate(inv.id)}
                        >
                          <Send size={16} className="mr-1" />
                          Issue
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <GenerateInvoiceModal isOpen={isGenerateOpen} onClose={() => setIsGenerateOpen(false)} />
    </div>
  );
};
